/**
 * Modelo de um guia gravado e dos seus passos, e os títulos automáticos (ao estilo
 * "Clique em «Guardar»"). Igual na extensão, na app web (fase 2) e nas exportações.
 */
import { formatDate, quote, type Locale } from '@nolens/i18n';
import type { Annotation } from './annotate';
import { guideText } from './guide-text';
import { hasPii, type PiiArea } from './pii';
import type { RunMark, RunValue } from './run';
import type { Target } from './target';

/**
 * navigate: abrir um endereço (início da gravação ou endereço escrito na barra);
 * click / dblclick; type: escrever num campo; key: Enter num campo; select: opção numa lista;
 * tab: mudar de separador; text: bloco de texto do autor (dica, aviso, informação, secção),
 * sem captura e sem número.
 * Só na app de Mac (ADR-G25): rightclick (clique com o botão direito); app: mudar de app ou
 * de janela; menu: escolha na barra de menus; shortcut: atalho de teclado (⌘S).
 */
export type StepKind =
  | 'navigate'
  | 'click'
  | 'dblclick'
  | 'rightclick'
  | 'type'
  | 'key'
  | 'select'
  | 'tab'
  | 'app'
  | 'menu'
  | 'shortcut'
  | 'text';

/** Onde o guia foi gravado: na extensão do Chrome ('web') ou na app de Mac. */
export type GuideSource = 'web' | 'mac';

/** Teclas de modificação de um atalho, pela ordem em que o macOS as mostra (⌃ ⌥ ⇧ ⌘). */
export type ShortcutModifier = 'ctrl' | 'alt' | 'shift' | 'cmd';
export const SHORTCUT_MODIFIERS: readonly ShortcutModifier[] = ['ctrl', 'alt', 'shift', 'cmd'];

/**
 * Atalho de teclado: as teclas de modificação e a tecla (uma letra ou um algarismo, em
 * maiúscula, ou o nome de uma tecla especial: return, tab, space, delete, escape, left,
 * right, up, down, home, end, pageup, pagedown, f1…f20). Sem modificação não é um atalho.
 */
export interface Shortcut {
  key: string;
  mods: ShortcutModifier[];
}

/** App do Mac em que o passo foi feito. */
export interface AppInfo {
  /** Identificador do bundle (com.microsoft.Excel). */
  bundleId: string;
  /** Nome que a app mostra (Microsoft Excel). */
  name: string;
  /** Ícone em data URL (PNG pequeno), só no 1.º passo de cada app (como o de um site). */
  icon?: string;
}

/** Estilo de um bloco de texto (nome na língua do guia: textToneLabel). */
export type TextTone = 'tip' | 'alert' | 'info' | 'heading';
export const TEXT_TONE_KEYS: readonly TextTone[] = ['tip', 'alert', 'info', 'heading'];

/** Ponto em fração do ecrã capturado (0…1). */
export interface Point {
  x: number;
  y: number;
}

/** Rectângulo em fração do ecrã capturado (0…1). */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Step {
  id: string;
  kind: StepKind;
  /** Título escrito pelo autor; vazio = título automático (autoTitle). */
  title: string;
  description: string;
  url: string;
  pageTitle: string;
  createdAt: number;
  /** Id da captura de ecrã (guardada à parte); ausente nos passos sem imagem. */
  imageId?: string;
  /** Tamanho do ecrã capturado, em píxeis CSS. */
  viewport?: { w: number; h: number };
  /** Onde a pessoa clicou (para o marcador e o zoom). */
  point?: Point;
  /** Caixa do elemento clicado (para o zoom). */
  box?: Box;
  target?: Target;
  /** Texto escrito (nunca de um campo sensível). */
  typed?: string;
  key?: 'Enter';
  option?: { text: string; value: string };
  tab?: { title: string };
  /** App de Mac: a app do passo (em todos os passos gravados no Mac). */
  app?: AppInfo;
  /** Passo «app» dentro da mesma app: a janela para onde se foi (título sem dados pessoais). */
  window?: { title: string };
  /** Passo «menu»: o caminho na barra de menus, do menu ao item («Ficheiro», «Exportar…»). */
  menu?: string[];
  /** Passo «shortcut». */
  shortcut?: Shortcut;
  /** Possíveis dados pessoais visíveis e a decisão do autor. */
  pii?: PiiArea[];
  piiDecision?: 'hidden' | 'kept';
  /** Zoom no ponto do clique (por omissão ligado nos passos com ponto). */
  zoom?: boolean;
  /** Recorte escolhido pelo autor (fração da captura); ganha ao zoom automático. */
  crop?: Box;
  /** Destaques e setas desenhados pelo autor por cima da captura. */
  annotations?: Annotation[];
  /** Site do passo (nome e ícone), guardado no 1.º passo de cada domínio do guia. */
  site?: SiteInfo;
  /** Captura original, guardada antes da primeira alteração (desfocar, substituir). */
  originalImageId?: string;
  /**
   * Só neste computador (não vai para a conta): o passo ficou sem captura. 'blind': não se
   * sabia onde tapar os dados da página, por isso não se tirou; 'failed': o Chrome não a deu.
   */
  noShot?: 'blind' | 'failed';
  /** Bloco de texto: estilo. */
  tone?: TextTone;
  /** Execução automática: quem faz o passo (ausente = sugestão automática, ver run.ts). */
  run?: RunMark;
  /** Execução automática: valor a escrever num passo «type» (nunca num campo sensível). */
  value?: RunValue;
  /**
   * Passo importado de um ficheiro (import.ts), ainda não confirmado numa página real: o
   * elemento é só uma descrição (texto, rótulo, papel). No guia interativo, a pessoa
   * aponta-o se não for encontrado e o passo fica com o elemento real; nunca é executado.
   */
  unverified?: true;
}

/** Nome e ícone de um site, para mostrar «<ícone> Nome» em vez do domínio. */
export interface SiteInfo {
  /** Nome do site (og:site_name, application-name), se a página o indicar. */
  name?: string;
  /** Ícone em data URL (PNG ou WebP pequeno). */
  icon?: string;
}

export interface Guide {
  id: string;
  /** Língua do guia (títulos automáticos, exportações); ausente nos guias antigos = pt-PT. */
  language?: Locale;
  /** Onde foi gravado; ausente = 'web' (extensão). */
  source?: GuideSource;
  title: string;
  description: string;
  createdAt: number;
  updatedAt: number;
  steps: Step[];
}

/** Endereço para mostrar: domínio + caminho, sem query nem fragmento (podem ter dados). */
export function displayUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    return u.host.replace(/^www\./, '') + path;
  } catch {
    return '';
  }
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Nome do elemento de um passo (texto, aria-label, title, nome do campo…). */
export function elementLabel(step: Pick<Step, 'target'>): string {
  const t = step.target;
  if (!t || t.sensitive) return '';
  return (t.text || t.ariaLabel || t.title || t.label || t.placeholder || '').trim().slice(0, 60);
}

function fieldName(step: Pick<Step, 'target'>): string {
  const t = step.target;
  if (!t) return '';
  return (t.label || t.ariaLabel || t.placeholder || t.name || '').trim().slice(0, 60);
}

const SHORTCUT_SYMBOLS: Record<ShortcutModifier, string> = {
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  cmd: '⌘',
};
const KEY_SYMBOLS: Record<string, string> = {
  return: '↩',
  tab: '⇥',
  delete: '⌫',
  forwarddelete: '⌦',
  escape: '⎋',
  left: '←',
  right: '→',
  up: '↑',
  down: '↓',
  home: '↖',
  end: '↘',
  pageup: '⇞',
  pagedown: '⇟',
};

/** Atalho como o macOS o mostra nos menus: «⇧⌘S», «⌘↩», «⌥Espaço». */
export function shortcutLabel(sc: Shortcut, locale: Locale): string {
  const mods = SHORTCUT_MODIFIERS.filter((m) => sc.mods.includes(m))
    .map((m) => SHORTCUT_SYMBOLS[m])
    .join('');
  const k = sc.key.toLowerCase();
  const key =
    k === 'space'
      ? guideText(locale).spaceKey
      : (KEY_SYMBOLS[k] ?? (/^f\d{1,2}$/.test(k) ? k.toUpperCase() : sc.key.toUpperCase()));
  return mods + key;
}

/** Título automático de um passo, na língua do guia. */
export function autoTitle(step: Step, locale: Locale): string {
  const t = guideText(locale);
  const q = (v: string) => quote(locale, v);
  const label = elementLabel(step);
  const field = fieldName(step);
  const inField = field ? q(field) : null;
  switch (step.kind) {
    case 'navigate':
      return displayUrl(step.url) ? t.open(displayUrl(step.url)) : t.openPage;
    case 'tab':
      return t.switchTab(q(step.tab?.title || step.pageTitle || hostOf(step.url) || t.nextTab));
    case 'app':
      if (step.window?.title) return t.switchWindow(q(step.window.title));
      return step.app?.name ? t.switchApp(q(step.app.name)) : t.switchOtherApp;
    case 'menu': {
      const path = (step.menu ?? []).map((m) => m.trim()).filter(Boolean);
      return path.length ? t.choose(path.join(' › ')) : t.chooseMenu;
    }
    case 'shortcut':
      return step.shortcut ? t.pressShortcut(shortcutLabel(step.shortcut, locale)) : t.pressKeys;
    case 'rightclick':
      return label ? t.rightclick(q(label)) : t.rightclickHere;
    case 'text':
      return t[step.tone || 'info'];
    case 'dblclick':
      return label ? t.dblclick(q(label)) : t.dblclickHere;
    case 'type':
      // Um e-mail, telefone ou NIF escrito nunca aparece no título (a captura já o tapa)
      if (step.typed && !hasPii(step.typed)) return t.type(q(step.typed), inField);
      return inField ? t.fill(inField) : t.fillField;
    case 'key':
      return t.pressEnter(inField);
    case 'select':
      return step.option?.text ? t.select(q(step.option.text), inField) : t.selectAny(inField);
    default:
      if (step.target?.tag === 'input' || step.target?.tag === 'textarea')
        return inField ? t.click(inField) : t.clickField;
      return label ? t.click(q(label)) : t.clickHere;
  }
}

/** Título a mostrar: o do autor ou o automático (na língua do guia). */
export function stepTitle(step: Step, locale: Locale): string {
  return step.title.trim() || autoTitle(step, locale);
}

/**
 * Onde se fez o passo: o site (num browser, também na app de Mac: o site diz mais do que
 * «Google Chrome») ou, sem endereço, a app do Mac.
 */
function placeOf(s: Pick<Step, 'url' | 'app'>): { host: string; app: boolean } | null {
  const h = hostOf(s.url);
  if (h) return { host: h, app: false };
  const id = s.app?.bundleId?.trim();
  return id ? { host: id, app: true } : null;
}

/** Aplicações usadas no guia (domínios, ou o bundle id das apps do Mac), por ordem de aparecimento. */
export function guideApps(guide: Pick<Guide, 'steps'>): string[] {
  const out: string[] = [];
  for (const s of guide.steps) {
    const h = placeOf(s)?.host;
    if (h && !out.includes(h)) out.push(h);
  }
  return out;
}

/** Domínios de vários níveis comuns (o nome do site é o rótulo antes deles). */
const SUFFIX2 = /\.(com|co|org|net|gov|edu|gob|ac)\.[a-z]{2}$/;

/** Nome de um site a partir do domínio: «app.oboticario.com.br» → «Oboticario». */
export function siteNameFromHost(host: string): string {
  const h = host.replace(/^www\./, '');
  const parts = h.replace(SUFFIX2, '').split('.');
  const label = parts.length > 1 ? parts[parts.length - 2]! : parts[0]!;
  if (!label || /^\d+$/.test(label)) return h;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Ícone aceite para um site: data URL de imagem PNG/WebP/JPEG/ICO pequena. */
export const isSiteIcon = (s: unknown): s is string =>
  typeof s === 'string' &&
  s.length <= 12000 &&
  /^data:image\/(png|webp|jpeg|x-icon|vnd\.microsoft\.icon);base64,[A-Za-z0-9+/=]+$/.test(s);

export interface GuideSite {
  /** Domínio do site, ou o bundle id de uma app do Mac. */
  host: string;
  name: string;
  icon?: string;
  /** Uma app do Mac (não um site). */
  app?: true;
}

/** Sites e apps do Mac usados no guia, por ordem, com o nome e o ícone guardados ao gravar. */
export function guideSites(guide: Pick<Guide, 'steps'>): GuideSite[] {
  const out = new Map<string, GuideSite>();
  for (const s of guide.steps) {
    const place = placeOf(s);
    if (!place) continue;
    const { host } = place;
    const cur: GuideSite = out.get(host) ?? { host, name: '', ...(place.app ? { app: true as const } : {}) };
    const info = place.app ? s.app : s.site;
    if (!cur.name && info?.name?.trim()) cur.name = info.name.trim().slice(0, 40);
    if (!cur.icon && isSiteIcon(info?.icon)) cur.icon = info!.icon;
    out.set(host, cur);
  }
  return [...out.values()].map((s) => ({
    ...s,
    name: s.name || (s.app ? s.host : siteNameFromHost(s.host)),
  }));
}

/**
 * Título por omissão de um guia: a partir da primeira aplicação usada (o nome que o site
 * indica, se foi guardado ao gravar; senão o domínio).
 */
export function defaultGuideTitle(
  guide: Pick<Guide, 'steps'>,
  locale: Locale,
  now = new Date(),
): string {
  const first = guide.steps.map(placeOf).find(Boolean);
  const host = first?.host;
  const named =
    host &&
    guide.steps.find((s) =>
      first.app
        ? !hostOf(s.url) && s.app?.bundleId === host && s.app.name?.trim()
        : hostOf(s.url) === host && s.site?.name?.trim(),
    );
  const info = named ? (first.app ? named.app : named.site) : null;
  const app = info?.name ? info.name.trim().slice(0, 40) : host;
  const t = guideText(locale);
  return app ? t.howToUse(app) : t.guideOf(formatDate(locale, now, { day: 'numeric', month: 'long' }));
}

/** É um passo com ação (numerado) e não um bloco de texto. */
export const isAction = (s: Pick<Step, 'kind'>) => s.kind !== 'text';

/** Número de cada passo (os blocos de texto não contam): id → 1, 2, 3… */
export function stepNumbers(guide: Pick<Guide, 'steps'>): Map<string, number> {
  const out = new Map<string, number>();
  let n = 0;
  for (const s of guide.steps) if (isAction(s)) out.set(s.id, ++n);
  return out;
}

export const actionCount = (guide: Pick<Guide, 'steps'>) => guide.steps.filter(isAction).length;

/** Duração estimada de leitura/execução (≈ 15 s por passo, mínimo 1 min). */
export function estimatedMinutes(guide: Pick<Guide, 'steps'>): number {
  return Math.max(1, Math.round((actionCount(guide) * 15) / 60));
}

/** Bloco de texto novo. */
export function textBlock(id: string, tone: TextTone = 'tip', now = Date.now()): Step {
  return {
    id,
    kind: 'text',
    tone,
    title: '',
    description: '',
    url: '',
    pageTitle: '',
    createdAt: now,
  };
}
